import { inject, Injectable } from '@angular/core';
import { BehaviorSubject, combineLatest, Observable, of, Subject } from 'rxjs';
import { debounceTime, map, startWith, switchMap, take } from 'rxjs/operators';
import { Subscribable } from '../../../../../models/base/subscribable';
import { EmployerPlanDesignatedUnit } from '../../../../../models/employers/plans/employer-plan-designated-unit';
import { EmployersDomainModel } from '../../../../../domainModels/employers-domain-model';
import { ToastService } from '../../../../../services/toast-service';
import { ODataQueryOptions } from '../../../../../models/shared/odata-query-options';
import { CustomError } from '../../../../../models/shared/custom-error';
import { DesignatedUnitsTableFilterRequest } from '../../../../../models/shared/odata-filter-requests/designated-units-table-filter-request';
import { DesignatedUnitsTableOrderbyRequest } from '../../../../../models/shared/odata-sorting-requests/designated-units-table-orderby-request';
import { DesignatedUnitTableType } from '../../../../../models/employers/enum/designated-unit-table-type';

@Injectable()
export class DesignatedUnitsTableCardTableService extends Subscribable {
  private employersDomainModel = inject(EmployersDomainModel);
  private toastService = inject(ToastService);

  private _planId = new BehaviorSubject<number | null>(null);
  public readonly planId$ = this._planId as Observable<number | null>;

  private _isLoading = new BehaviorSubject<boolean>(true);
  public readonly isLoading$ = this._isLoading as Observable<boolean>;

  private _displayedData = new BehaviorSubject<EmployerPlanDesignatedUnit[] | null>(null);
  public readonly displayedData$ = this._displayedData as Observable<EmployerPlanDesignatedUnit[] | null>;

  private _totalItems = new BehaviorSubject<number>(0);
  public readonly totalItems$ = this._totalItems as Observable<number>;

  private _activeTabCount = new BehaviorSubject<number | null>(null);
  public readonly activeTabCount$ = this._activeTabCount as Observable<number | null>;

  private _inactiveTabCount = new BehaviorSubject<number | null>(null);
  public readonly inactiveTabCount$ = this._inactiveTabCount as Observable<number | null>;

  private _skip = new BehaviorSubject<number>(0);
  public readonly skip$ = this._skip as Observable<number>;
  public setSkip = (skip: number): void => this._skip.next(skip);

  public pageSize$ = of(10);

  private _activeFilter = new BehaviorSubject<DesignatedUnitTableType>(DesignatedUnitTableType.Active);
  public readonly activeFilter$ = this._activeFilter as Observable<DesignatedUnitTableType>;
  public setActiveFilter = (filter: DesignatedUnitTableType): void => this._activeFilter.next(filter);

  private _activeSort = new BehaviorSubject<string>('clientUnitId asc');
  public readonly activeSort$ = this._activeSort as Observable<string>;
  public updateActiveSort = (sortString: string): void => this._activeSort.next(sortString);

  private _refetch = new Subject<void>();

  private fetchInactiveTabCount = this.planId$
    .notNull()
    .pipe(take(1))
    .subscribeWhileAlive({
      owner: this,
      next: planId => {
        const odataOptions = new ODataQueryOptions();
        const filterReq = new DesignatedUnitsTableFilterRequest();
        filterReq.activeUnits = false;
        const filterString = filterReq.getFilterString();
        if (filterString) {
          odataOptions.setFilter(filterString);
        }
        odataOptions.setTop(0);
        odataOptions.setCount(true);

        this.employersDomainModel
          .getEmployerPlanDesignatedUnits(planId ?? 0, odataOptions)
          .pipe(take(1))
          .subscribe({
            next: res => {
              this._inactiveTabCount.next(res['@odata.count'] ?? 0);
            },
            error: (err: CustomError) => {
              this.toastService.publishError(err);
            }
          });
      }
    });

  private odataQueryOptions$ = combineLatest([
    this.planId$.notNull(),
    this.activeFilter$,
    this.activeSort$,
    this.skip$,
    this.pageSize$,
    this._refetch.pipe(startWith(undefined))
  ]).pipe(
    map(([planId, activeFilter, activeSort, skip, pageSize]) => {
      const odataOptions = new ODataQueryOptions();
      const filterReq = new DesignatedUnitsTableFilterRequest();
      filterReq.activeUnits = activeFilter === DesignatedUnitTableType.Active;
      const filterString = filterReq.getFilterString();
      if (filterString) {
        odataOptions.setFilter(filterString);
      }

      if (activeSort) {
        const orderByReq = new DesignatedUnitsTableOrderbyRequest(activeSort);
        const orderByString = orderByReq.getOrderByString();
        if (orderByString) {
          odataOptions.setOrderBy(orderByString);
        }
      }

      odataOptions.setSkip(skip);
      odataOptions.setTop(pageSize);
      odataOptions.setCount(true);
      odataOptions.setExpand(
        'DesignatedUnitType, RemittanceFrequencyType, Versions($expand=ContributionRateBasisType, MatchedContributionRateType, UnmatchedContributionRateType, LessThanFullTimeParticipationType)'
      );
      return { planId, activeFilter, odataOptions };
    })
  );

  private listenToOdataOptions = this.odataQueryOptions$.pipe(debounceTime(200)).subscribeWhileAlive({
    owner: this,
    next: ({ planId, activeFilter, odataOptions }) => {
      this.getData(planId ?? 0, activeFilter, odataOptions);
    }
  });

  private resetSkipOnFilterOrSortChange = combineLatest([this.activeFilter$, this.activeSort$]).subscribeWhileAlive({
    owner: this,
    next: () => {
      this._skip.next(0);
    }
  });

  public setPlanId(planId: number): void {
    this._planId.next(planId);
  }

  public refetch(): void {
    this._refetch.next();
  }

  public getAllDesignatedUnits(): Observable<EmployerPlanDesignatedUnit[]> {
    return this.planId$.notNull().pipe(
      take(1),
      switchMap(planId => {
        return this.employersDomainModel.getEmployerPlanDesignatedUnits(planId ?? 0, new ODataQueryOptions());
      }),
      map(res => res.value ?? [])
    );
  }

  private getData(planId: number, activeFilter: DesignatedUnitTableType, odataOptions: ODataQueryOptions): void {
    this._isLoading.next(true);
    this.employersDomainModel
      .getEmployerPlanDesignatedUnits(planId, odataOptions)
      .pipe(take(1))
      .subscribe({
        next: res => {
          const count = res['@odata.count'] ?? 0;
          this._totalItems.next(count);
          this._displayedData.next(res.value);
          this._isLoading.next(false);
          if (activeFilter === DesignatedUnitTableType.Active) {
            this._activeTabCount.next(count);
          } else {
            this._inactiveTabCount.next(count);
          }
        },
        error: (err: CustomError) => {
          this.toastService.publishError(err);
          this._isLoading.next(false);
        }
      });
  }
}
